import experiment from 'app/gamma/src/util/experiments';
import React, { useState } from 'react';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { MoonshotBox } from './MoonshotBox';
import styles from './MoonshotSuccess.less';
import { Spinner } from 'app/src/components/Spinner';
import {
  Button,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import {
  useMoonshotSuccessOrgCreditsQuery,
  useMoonshotSuccessQuery,
} from '@trello/graphql/generated';
import { MoonshotSubmitFunction } from './types';
import { forNamespace } from 'app/common/lib/util/i18n';

const format = forNamespace(['moonshot', 'success']);

interface MoonshotSuccessProps {
  onSubmit: MoonshotSubmitFunction;
}

export const MoonshotSuccess: React.FC<MoonshotSuccessProps> = ({
  onSubmit,
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const slackPromoCode = urlParams.get('promocode');
  const isFreeTrialExperiment =
    useFeatureFlag('btg.bc-free-trial', false) &&
    // We don't want users that are signing up with Slack promocode get bucketed into the Free trial experiment.
    // Once the get the experiment code runs, it will have a side effect of bucketing users to it.
    // However, we still want to use the Moonshot flow.
    !slackPromoCode &&
    experiment.get('moonshotDirectSignup').get('variation') === 'enabled';
  const [loading, setLoading] = useState(false);
  const { data: dataMe, loading: loadingMe } = useMoonshotSuccessQuery({
    variables: {
      memberId: 'me',
    },
  });

  // get the oldest org that a user is an admin of
  const team = dataMe?.member?.organizations?.find((org) => {
    const myMembership = org?.memberships?.find((membership) => {
      return membership.idMember === dataMe?.member?.id;
    });

    return myMembership?.memberType === 'admin';
  });

  const {
    data: dataCredits,
    loading: loadingCredits,
  } = useMoonshotSuccessOrgCreditsQuery({
    variables: {
      orgId: team?.id ?? '',
    },
    skip: loadingMe || !isFreeTrialExperiment,
  });

  if (loadingMe || loadingCredits) {
    return (
      <MoonshotBox className={styles.box}>
        <Spinner centered />
      </MoonshotBox>
    );
  }

  const teamName = team?.displayName ?? '';

  const nextStep: React.FormEventHandler = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);
    await onSubmit();
    setLoading(false);
  };

  return (
    <>
      <MoonshotBox className={styles.box}>
        <div className={styles.headerWrapper}>
          <img
            src={require('resources/images/moonshot/tacoCompletionImage.svg')}
            alt=""
          />

          {isFreeTrialExperiment ? (
            <>
              <p>{format('free trial success heading part 1', { teamName })}</p>
              <h1>{format('free trial success heading part 2')}</h1>
            </>
          ) : (
            <>
              <h1>{format('success heading part 1')}</h1>
              <p>{format('success heading part 2', { teamName })}</p>
            </>
          )}
        </div>
        {isFreeTrialExperiment &&
        (dataCredits?.organization?.credits?.length ?? 0) > 0 ? (
          <p className={styles.content}>
            {format('free trial success messaging')}
          </p>
        ) : null}
        <Button
          className={styles.button}
          size={ButtonSizes.FULLWIDTH}
          onClick={nextStep}
          disabled={loading}
        >
          {loading ? (
            <Spinner centered wrapperClassName={styles.spinner} />
          ) : isFreeTrialExperiment ? (
            format('free trial continue button')
          ) : (
            format('continue button')
          )}
        </Button>
      </MoonshotBox>
      {!isFreeTrialExperiment ? (
        <div className={styles.bannerWrapper}>
          <a
            data-test-id="googleStoreLink"
            href="https://play.google.com/store/apps/details?id=com.trello"
            target="_blank"
          >
            <img
              className={styles.playStoreBanner}
              alt={format('google app store image')}
              src={require('resources/images/buttons/btn-playstore-2.png')}
            />
          </a>
          <a
            data-test-id="appleStoreLink"
            href="https://apps.apple.com/us/app/trello-organize-anything/id461504587"
            target="_blank"
          >
            <img
              className={styles.appStoreBanner}
              alt={format('apple app store image')}
              src={require('resources/images/buttons/btn-appstore-2.svg')}
            />
          </a>
        </div>
      ) : null}
    </>
  );
};
